import topbar from 'topbar';

/**
 * Configuration options for the topbar progress indicator.
 */
export interface TopbarOptions {
  /** Bar color (hex format, e.g., "#29d") */
  barColor?: string;
  /** Shadow color (rgba format, e.g., "rgba(0, 0, 0, .3)") */
  shadowColor?: string;
}

/**
 * Reads a CSS custom property value from the document root.
 */
function getCSSProperty(propertyName: string, fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback;
  }

  const value = getComputedStyle(document.documentElement).getPropertyValue(propertyName).trim();

  return value !== '' ? value : fallback;
}

/**
 * Configure and initialize the topbar progress indicator.
 * Shows during LiveView navigation and form submissions.
 *
 * Colors can be configured via:
 * 1. Options parameter (highest priority)
 * 2. CSS custom properties: --topbar-color, --topbar-shadow
 * 3. Default hardcoded values (fallback)
 *
 * @returns A cleanup function that removes the event listeners
 */
export function initTopbar(options?: TopbarOptions): () => void {
  const defaultBarColor = '#29d';
  const defaultShadowColor = 'rgba(0, 0, 0, .3)';

  const barColor = options?.barColor ?? getCSSProperty('--topbar-color', defaultBarColor);
  const shadowColor = options?.shadowColor ?? getCSSProperty('--topbar-shadow', defaultShadowColor);

  topbar.config({
    barColors: { 0: barColor },
    shadowColor: shadowColor,
  });

  const handlePageLoadingStart = (): void => {
    topbar.show(300);
  };
  const handlePageLoadingStop = (): void => {
    topbar.hide();
  };

  window.addEventListener('phx:page-loading-start', handlePageLoadingStart);
  window.addEventListener('phx:page-loading-stop', handlePageLoadingStop);

  return (): void => {
    window.removeEventListener('phx:page-loading-start', handlePageLoadingStart);
    window.removeEventListener('phx:page-loading-stop', handlePageLoadingStop);
  };
}
